import { g as getMediaQuery } from './media-queries-5a9283a8.js';
import { s as srraf } from './srraf.es-487187f3.js';
import { i as intersectionWatcher } from './intersection-watcher-50753dc4.js';

const atBreakpointChange = (breakpointToWatch, callback) => {
  const _screenUnderBP = () => {
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;

    return viewportWidth <= breakpointToWatch
  };

  let screenUnderBP = _screenUnderBP();

  const widthWatcher = srraf(({ vw }) => {
    const currentScreenWidthUnderBP = vw <= breakpointToWatch;

    if (currentScreenWidthUnderBP !== screenUnderBP) {
      screenUnderBP = currentScreenWidthUnderBP;
      return callback()
    }
  });

  const unload = () => {
    widthWatcher.destroy();
  };

  return { unload }
};

const prefersReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
};

/**
 * Extracts background videos from 'template's to be added to the DOM, and controls pause/play state through
 * a11y triggers and an intersection observer.
 * If two videos are enabled for desktop and mobile, update video templates by screen size.
 */

class BackgroundVideo extends HTMLElement {
  connectedCallback() {
    const videoTemplate = this.querySelector("[js-background-video-template]");

    const hasVideo = this.dataset.hasVideo === "true";
    const hasMobileVideo = this.dataset.hasMobileVideo === "true";

    if (hasVideo && !hasMobileVideo) {
      this.#setVideoTemplate(videoTemplate, this);
    } else if (hasVideo & hasMobileVideo) {
      const videoMobileTemplate = this.querySelector(
        "[js-mobile-background-video-template]",
      );
      this.#initResponsiveVideoTemplate(
        this,
        videoTemplate,
        videoMobileTemplate,
      );
    }
  }

  disconnectedCallback() {
    this.removeEventListener("toggle", this.handleToggle, true);
  }

  #initResponsiveVideoTemplate(container, videoTemplate, videoMobileTemplate) {
    const setTemplateByWidth = () => {
      if (window.matchMedia(getMediaQuery("tablet")).matches) {
        this.#setVideoTemplate(videoTemplate, container);
      } else {
        this.#setVideoTemplate(videoMobileTemplate, container);
      }
    };

    setTemplateByWidth();
    atBreakpointChange(720, setTemplateByWidth);
  }

  #setVideoTemplate(videoTemplate, container) {
    const pauseBtn = container.querySelector("[js-video-pause-button]");
    const muteBtn = container.querySelector("[js-video-mute-button]");
    const currentVideoContainer = container.querySelector(
      "[js-current-background-video-container]",
    );

    const currentVideoTemplate = videoTemplate.content.cloneNode(true);
    const currentVideoEl = currentVideoTemplate.querySelector(
      "[data-background-video]",
    );

    if (currentVideoEl) {
      currentVideoEl.muted = true;
    }

    currentVideoContainer.innerHTML = "";
    currentVideoContainer.appendChild(currentVideoTemplate);

    // Callback for intersectionWatcher helps leverage context from this file without needing to pass it
    const videoWatcherCallback = (visible) =>
      this.#visibleVideoWatcher(currentVideoEl, pauseBtn, visible);

    this.#setPlayPause(currentVideoEl, pauseBtn, "pause");
    this.#videoA11yHandler(currentVideoEl, pauseBtn);
    this.#videoMuteHandler(currentVideoEl, muteBtn);
    intersectionWatcher(currentVideoEl, { callback: videoWatcherCallback });
  }

  #videoA11yHandler(video, pauseBtn) {
    if (!pauseBtn || !video) return

    // Play/pause button will show when tabbing
    pauseBtn.addEventListener("click", (e) => {
      e.preventDefault();

      // Pausing via a11y tabbing is respected as priority state by the intersection observer
      if (video.paused) {
        this.#setPlayPause(video, pauseBtn, "play");
        video.dataset.a11yPriorityPause = false;
      } else {
        this.#setPlayPause(video, pauseBtn, "pause");
        video.dataset.a11yPriorityPause = true;
      }
    });
  }

  #videoMuteHandler(video, muteBtn) {
    if (!muteBtn || !video) return

    // Mute/unmute button toggles audio
    muteBtn.addEventListener("click", (e) => {
      e.preventDefault();

      if (video.muted) {
        this.#setMuteUnmute(video, muteBtn, "unmute");
      } else {
        this.#setMuteUnmute(video, muteBtn, "mute");
      }
    });
  }

  #visibleVideoWatcher(video, pauseBtn, visible) {
    // Prioritize browser and device settings
    if (prefersReducedMotion()) {
      return
    }

    if (visible && video.paused) {
      // Fade in video from poster for smooth transition, only on first entry
      if (video.dataset.videoLoaded === "false") {
        video.classList.add("background-video-fade-in");
        video.dataset.videoLoaded = "true";
      }

      // Play via intersection entry should only apply when paused state not from a11y tabbing
      if (video.dataset.a11yPriorityPause === "false") {
        this.#setPlayPause(video, pauseBtn, "play");
      }
    }

    if (!visible && !video.paused) {
      this.#setPlayPause(video, pauseBtn, "pause");
    }
  }

  #setPlayPause(video, pauseBtn, state) {
    const {
      strings: { accessibility: strings },
      icons,
    } = window.theme;

    if (state === "pause") {
      video.pause();

      if (pauseBtn.dataset.pauseButtonContent === "icon") {
        pauseBtn.innerHTML = icons.play;
      } else {
        pauseBtn.innerText = strings.playVideo;
      }
    } else {
      video.play();

      if (pauseBtn.dataset.pauseButtonContent === "icon") {
        pauseBtn.innerHTML = icons.pause;
      } else {
        pauseBtn.innerText = strings.pauseVideo;
      }
    }
  }

  #setMuteUnmute(video, muteBtn, state) {
    const volumeMuteSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width="20" height="20"><path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
    const volumeOnSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width="20" height="20"><path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.07 4.93C20.9447 6.80528 21.9979 9.34836 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    if (state === "mute") {
      video.muted = true;

      if (muteBtn.dataset.muteButtonContent === "icon") {
        muteBtn.innerHTML = volumeMuteSVG;
      } else {
        muteBtn.innerText = "Unmute";
      }
    } else {
      video.muted = false;

      if (muteBtn.dataset.muteButtonContent === "icon") {
        muteBtn.innerHTML = volumeOnSVG;
      } else {
        muteBtn.innerText = "Mute";
      }
    }
  }
}

if (!customElements.get("background-video")) {
  customElements.define("background-video", BackgroundVideo);
}
